import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { cn } from "@/lib/utils";

interface DanceStyleCheckboxesProps {
  selectedStyles: string[];
  onStyleChange: (styles: string[]) => void;
}

export function DanceStyleCheckboxes({
  selectedStyles,
  onStyleChange,
}: DanceStyleCheckboxesProps) {
  const { styles, groups } = useDanceStyles();

  const handleToggle = (slug: string) => {
    if (selectedStyles.includes(slug)) {
      onStyleChange(selectedStyles.filter((s) => s !== slug));
    } else {
      onStyleChange([...selectedStyles, slug]);
    }
  };

  const handleSelectAll = () => {
    onStyleChange([]);
  };

  const allSelected = selectedStyles.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Dance Styles</h3>
        {!allSelected && (
          <button
            onClick={handleSelectAll}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="space-y-4">
        {groups.map((group) => {
          const groupStyles = styles.filter((s) => s.group === group);
          if (groupStyles.length === 0) return null;
          return (
            <div key={group}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {group}
              </p>
              <div className="space-y-2">
                {groupStyles.map((style) => {
                  const isChecked = selectedStyles.includes(style.slug);
                  return (
                    <div key={style.slug} className="flex items-center gap-3">
                      <Checkbox
                        id={`style-${style.slug}`}
                        checked={isChecked}
                        onCheckedChange={() => handleToggle(style.slug)}
                        className={cn(
                          "border-2 transition-colors",
                          isChecked && "border-transparent"
                        )}
                        style={
                          isChecked
                            ? { backgroundColor: style.color, borderColor: style.color }
                            : { borderColor: style.color }
                        }
                      />
                      <Label
                        htmlFor={`style-${style.slug}`}
                        className="text-sm font-medium cursor-pointer flex items-center gap-2"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: style.color }}
                        />
                        {style.name}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
